import React, { useEffect, useMemo, useRef, useState } from "react";
import { TableRow, TableCell, Typography, IconButton, Tooltip, TextField, Avatar } from "@mui/material";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IniInfo } from "system/types/interfaces";
import { ConfirmationDialog } from "components/ui";
import { Icon } from "@fluentui/react/lib/Icon";
import { isValidRule } from "../../../utils";
import { Roles } from "system/types/enums/Roles";
import { DeleteRegular, EditRegular, RulerFilled } from "@fluentui/react-icons";

interface SortableRowProps {
  id: string;          
  ruleId: string;      
  rule: IniInfo;
  index: number;
  style?: React.CSSProperties;
  modifiedRows: string[];
  onAdd: (index: number) => void;
  onDelete: (index: number) => void;
  onUndoDelete: (index: number) => void;
  onGoToSubject: (rule: IniInfo, index: number) => void;
  onEditComment: (rule: IniInfo, index: number) => void;
  searchTerm: string;
  onReorder: (oldIndex: number, newIndex: number) => void;
  totalRows: number;
  packageIcon: string;
  onToggleComment: (id: string, newContent: string, isComment: boolean) => void;
  readOnlyViewer: boolean;
}

export const SortableRow = React.memo(
  ({
    id,
    ruleId,
    rule,
    index,
    style,
    modifiedRows,
    onAdd,
    onDelete,
    onUndoDelete,
    onGoToSubject,
    onEditComment,
    searchTerm,
    onReorder,
    totalRows,
    packageIcon,
    onToggleComment,
    readOnlyViewer,
  }: SortableRowProps) => {
    const isModified = modifiedRows.includes(ruleId);
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id,   
    disabled: readOnlyViewer, 
    });

    const [confirmationDialogVisible, setConfirmationDialogVisible] = useState(false);
    const [isEditingRowNumber, setIsEditingRowNumber] = useState(false);
    const [rowNumberInput, setRowNumberInput] = useState<string>((index + 1).toString());

    const nameRef = useRef<HTMLDivElement>(null);
    const valueRef = useRef<HTMLDivElement>(null);
    const commentRef = useRef<HTMLDivElement>(null);

    const [isNameOverflowing, setIsNameOverflowing] = useState(false);
    const [isValueOverflowing, setIsValueOverflowing] = useState(false);
    const [isCommentOverflowing, setIsCommentOverflowing] = useState(false);

    const isComment = rule.isComment ?? false;
    const isDeleted = rule.isDeleted || false;

    const rowStyle: React.CSSProperties = {
      ...style,
      transform: CSS.Transform.toString(transform),
      transition,
      display: "grid",
      gridTemplateColumns: isComment ? "5% 5% 65% 0% 25%" : "5% 5% 25% 40% 25%",
      alignItems: "center",
      borderBottom: "0.5px solid #515151",
      placeContent: "center",
      backgroundColor: isComment ? "#1e1e1e" : isDeleted ? "#2a2a2a" : "inherit",
      height: "50px",
      overflow: "hidden",
    };

    const escapeRegExp = (string: string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    };

    const highlightText = (text?: string) => {
      if (!searchTerm || !text) return text;
      try {
        const escapedSearchTerm = escapeRegExp(searchTerm);
        const parts = text.split(new RegExp(`(${escapedSearchTerm})`, "gi"));
        return parts.map((part, i) =>
          part.toLowerCase() === searchTerm.toLowerCase() ? (
            <span key={i} style={{ backgroundColor: "yellow", color: "black" }}>
              {part}
            </span>
          ) : (
            part
          )
        );
      } catch (error) {
        return text;
      }
    };

    const scrollCellStyles = {
      "&::-webkit-scrollbar": {
        height: "4px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#555",
        borderRadius: "4px",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        height: "8px",
        backgroundColor: "#ccc",
      },
    };

    const tooltipStyles = {
      backgroundColor: "#333",
      color: "#fff",
      fontSize: "12px",
      padding: "8px",
      borderRadius: "4px",
      maxWidth: "500px",
      whiteSpace: "normal",
      wordBreak: "break-word",
    };

    useEffect(() => {
      const checkOverflow = (ref: React.RefObject<HTMLDivElement>, setOverflow: (overflow: boolean) => void) => {
        if (ref.current) {
          const { scrollWidth, clientWidth } = ref.current;
          setOverflow(scrollWidth > clientWidth);
        }
      };

      if (isComment) {
        checkOverflow(commentRef, setIsCommentOverflowing);
      } else {
        checkOverflow(nameRef, setIsNameOverflowing);
        checkOverflow(valueRef, setIsValueOverflowing);
      }
    }, [isComment, rule.name, rule.delimiter, rule.value, rule.inlineComment]);

    const handleRowNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowNumberInput(event.target.value);
    };

    const handleRowNumberConfirm = () => {
      const newRowNumber = parseInt(rowNumberInput, 10);
      const currentRowNumber = index + 1;

      if (isNaN(newRowNumber) || newRowNumber < 1 || newRowNumber > totalRows) {
        setRowNumberInput(currentRowNumber.toString());
      } else if (newRowNumber !== currentRowNumber) {
        const oldIndex = index;
        const newIndex = newRowNumber - 1;
        onReorder(oldIndex, newIndex);
      }

      setIsEditingRowNumber(false);
    };

    const handleRowNumberClick = () => {
      if (readOnlyViewer) return;
      setIsEditingRowNumber(true);
    };

    const handleRowNumberKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.stopPropagation();
        handleRowNumberConfirm();
      }
    };

    const canUncomment = useMemo(() => {
      if (!isComment) return true;
      return isValidRule(rule.value ?? "");
    }, [isComment, rule.value]);

    const handleToggleComment = () => {
      if (isComment) {
        if (!canUncomment) return;
        onToggleComment(id, "", false);
      } else {
        const currentContent = `${rule.name} ${rule.delimiter} ${rule.value} ${rule.inlineComment ? ` ;${rule.inlineComment}` : ""}`;
        const commentedContent = `; ${currentContent.trim()}`;
        onToggleComment(id, commentedContent, true);
      }
    };

    return (
      <>
        <TableRow
          id={`row-${index}`}
          component="div"
          ref={setNodeRef}
          style={rowStyle}
          sx={{
            "&:hover": {
              backgroundColor: isComment ? "#2a2a2a" : isDeleted ? "#2a2a2a" : "#333",
            },
            height: "auto",
          }}
        >
          {/* Column 1: Drag and drop */}
          <TableCell
            component="div"
            {...listeners}
            {...attributes}
            sx={{
              cursor: "grab",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderStyle: "none",
              visibility: readOnlyViewer ? "hidden" : "visible",
            }}
          >
            <Tooltip title="Drag to reorder" arrow>
              <span><Icon iconName="GripperBarHorizontal" style={{ color: isComment ? "#4caf50" : "#757575" }} /></span>
            </Tooltip>
          </TableCell>

          {/* Column 2: Secuence */}
          <TableCell
            component="div"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderStyle: "none",
              cursor: "pointer",
            }}
          >
            {isEditingRowNumber ? (
              <TextField
                value={rowNumberInput}
                onChange={handleRowNumberChange}
                onBlur={handleRowNumberConfirm}
                onKeyDown={handleRowNumberKeyDown}
                autoFocus
                variant="standard"
                type="number"
                sx={{
                  fontSize: "16px",
                  width: "40px",
                  "& .MuiInput-underline:before": {
                    borderBottomColor: "#757575",
                  },
                  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                    borderBottomColor: "#fff",
                  },
                  "& .MuiInput-underline:after": {
                    fontSize: "16px",
                    borderBottomColor: "#4caf50",
                  },
                }}
                inputProps={{
                  min: 1,
                  max: totalRows,
                  style: { padding: 0, fontSize: "12px" },
                }}
              />
            ) : (
              <Typography
                onClick={handleRowNumberClick}
                sx={{
                  color: "#fff",
                  fontSize: "14px",
                  border: "1px solid #6b6b6b",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  "&:hover": {
                    color: "#4caf50",
                  },
                }}
              >
                {index + 1}
              </Typography>
            )}
          </TableCell>

          {/* Column 3: Text */}
          {isComment ? (
            <>
              <TableCell
                ref={commentRef}
                component="div"
                sx={{
                  overflowY: "hidden",
                  overflowX: "auto",
                  whiteSpace: "nowrap",
                  borderStyle: "none",
                  lineHeight: 1.5,
                  height: "50px",
                  display: "flex",
                  alignItems: "center",
                  ...scrollCellStyles,
                }}
              >
                <Tooltip title={rule.value || ""} disableHoverListener={!isCommentOverflowing} sx={tooltipStyles}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: isDeleted ? "#d5878b" : "#4caf50",
                      fontStyle: "italic",
                      textDecoration: isDeleted ? "line-through" : "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {highlightText(rule.value || "")}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell
                component="div"
                sx={{
                  overflowX: "auto",
                  whiteSpace: "nowrap",
                  borderStyle: "none",
                  lineHeight: 1.5,
                }}
              ></TableCell>
            </>
          ) : (
            <>
              <TableCell
                component="div"
                ref={nameRef}
                sx={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  whiteSpace: "nowrap",
                  borderStyle: "none",
                  lineHeight: 1.5,
                  height: "50px",
                  display: "flex",
                  alignItems: "center",
                  ...scrollCellStyles,
                }}
              >
                {packageIcon && packageIcon.trim() !== "" ? (
                  <Avatar
                    src={packageIcon}
                    alt="Package Icon"
                    sx={{
                      width: 24,
                      height: 24,
                      marginRight: "8px",
                      borderRadius: "0%",
                    }}
                  />
                ) : (
                  <Avatar
                    src="/img/DefaultPackage.png"
                    alt="Package Icon"
                    sx={{
                      width: 24,
                      height: 24,
                      marginRight: "8px",
                      borderRadius: "0%",
                    }}
                  />
                )}
                <Tooltip title={rule.name || ""} disableHoverListener={!isNameOverflowing} sx={tooltipStyles}>
                  <Typography
                    variant="body1"
                    sx={{ textDecoration: isDeleted ? "line-through" : "none", color: isDeleted ? "#d5878b" : "#fff" }}
                  >
                    {highlightText(rule.name)}
                  </Typography>
                </Tooltip>
              </TableCell>

              <TableCell
                component="div"
                ref={valueRef}
                sx={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  whiteSpace: "nowrap",
                  borderStyle: "none",
                  lineHeight: 1.5,
                  height: "50px",
                  display: "flex",
                  alignItems: "center",
                  ...scrollCellStyles,
                }}
              >
                <Tooltip
                  title={`${rule.delimiter} ${rule.value}${rule.inlineComment ? `;${rule.inlineComment}` : ""}`}
                  disableHoverListener={!isValueOverflowing}
                  sx={tooltipStyles}
                >
                  <Typography
                    variant="body1"
                    sx={{ textDecoration: isDeleted ? "line-through" : "none", color: isDeleted ? "#d5878b" : "#fff" }}
                  >
                    {highlightText(rule.delimiter)} {highlightText(rule.value)}
                    {rule.inlineComment && (
                      <Typography variant="caption" style={{ color: "#4caf50", fontStyle: "italic" }}>
                        {";" + highlightText(rule.inlineComment)}
                      </Typography>
                    )}
                  </Typography>
                </Tooltip>
              </TableCell>
            </>
          )}

          {/* Column 4: Buttons */}
          <TableCell
            component="div"
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "8px",
              borderStyle: "none",
              justifySelf: "flex-end",
              height: "50px",
            }}
          >
            {isModified && (
              <Typography
                variant="body2"
                sx={{
                  backgroundColor: isDeleted ? "#d5878b" : "#fbc02d",
                  color: "#000",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontWeight: "bold",
                }}
              >
                {isDeleted ? "Deleted Row" : "Pending Changes"}
              </Typography>
            )}

            {/* Toggle Comment Button */}
            <Tooltip
              title={
                isComment
                  ? canUncomment
                    ? "Uncomment Rule"
                    : "Cannot uncomment: the comment does not contain a valid rule. Edit or delete it instead."
                  : "Comment Rule"
              }
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  aria-label={isComment ? "Uncomment rule" : "Comment rule"}
                  onClick={handleToggleComment}
                  hidden={readOnlyViewer}
                  disabled={isComment && !canUncomment}
                  sx={{
                    color: isComment ? (canUncomment ? "#4caf50" : "#8a8886") : "#8a8886",
                    "&:hover": {
                      color: isComment ? (canUncomment ? "#66bb6a" : "#8a8886") : "#bdbdbd",
                    },
                  }}
                >
                  {isComment ? <Icon iconName="CommentAdd" style={{ fontSize: "small" }} /> : <Icon iconName="Comment" style={{ fontSize: "small" }} />}
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Add line">
              <IconButton size="small" aria-label="Add line" color="primary" onClick={() => onAdd(index)} hidden={readOnlyViewer}>
                <Icon iconName="Add" />
              </IconButton>
            </Tooltip>
            {isDeleted ? (
              <Tooltip title="Undo Delete" arrow>
                <IconButton size="small" aria-label="Undo delete" onClick={() => onUndoDelete(index)}>
                  <Icon iconName="Undo" />
                </IconButton>
              </Tooltip>
            ) : (
              <>
                <Tooltip title="Delete" arrow>
                  <IconButton
                    aria-label="Delete rule"
                    size="small"
                    color="error"
                    hidden={readOnlyViewer}
                    sx={{ color: isComment ? "#d54c52" : "#d54c52" }}
                    onClick={() => setConfirmationDialogVisible(true)}
                  >
                    <DeleteRegular fontSize={22} style={{ color: "#d5878b" }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={isComment ? "Edit Comment" : "Edit Rule"} arrow>
                  <IconButton
                    aria-label={isComment ? "Edit comment" : "Edit rule"}
                    size="small"
                    sx={{ color: "#8a8886" }}
                    hidden={isComment && readOnlyViewer}
                    onClick={() => (isComment ? onEditComment(rule, index) : onGoToSubject(rule, index))}
                  >
                    <EditRegular fontSize={22} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </TableCell>
        </TableRow>


        <ConfirmationDialog
          title="Delete Rule"
          subText={`Are you sure you want to delete the rule?`}
          isVisible={confirmationDialogVisible}
          onConfirm={() => {
            setConfirmationDialogVisible(false);
            onDelete(index);
          }}
          onCancel={() => {
            setConfirmationDialogVisible(false);
          }}
        />
      </>
    );
  }
);
